import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { graphql, Link, PageProps } from 'gatsby';
import { Link as GatsbyLink } from 'gatsby-link';

import { Grid2 as Grid, styled } from '@mui/material';

import {
  Market,
  ServiceSegmentType,
  ServiceTeaserData,
  ServiceTeaserType,
} from '@mewa/types';
import {
  BreadCrumbData,
  BreadCrumbs,
  Line,
  MewaCircle,
  MewaCircleData,
  ResponsiveGrid,
  ResponsiveTabs,
  SeoFooter,
  ServiceTeaser,
} from '@mewa/ui';

import { localeBaseUrl } from '../../../libs/alternative-links';
import t from '../../../libs/translate';
import { useConversionButtonLabel } from '../../a-b-testing/cta-label-experiment';
import { ContactTeaserWrapper } from '../../components/contact-teaser/contact-teaser-wrapper';
import { CustomerReport } from '../../components/customer-report/types';
import { NavigationData } from '../../components/header/types';
import { PageHeadlineWrapper } from '../../components/page-headline-wrapper';
import { CollectionSliderWrapper } from '../../components/slider/collection-slider-wrapper';
import { ProductCardBaseData } from '../../components/slider/product-card-base';
import { ProductsSliderContainer } from '../../components/slider/products-slider';
import {
  CollectionProducts,
  groupByCollection,
} from '../../helpers/product-grouping';
import { protectiveClothingIconForType } from '../../helpers/protective-clothing-icons';
import { GlobalPageContext } from '../../types';
import { storeServiceSegment } from '../conversion/persistence';
import { ContentSection } from '../dynamic-content-page/content-section';
import { PossibleContentSections } from '../dynamic-content-page/fragment-and-types';
import { possibleComponents } from './content-section';

export { Head } from '../../layouts/head';

type Props = NavigationData & {
  pimServiceSegment: {
    name: string;
    slug: string;
    claim: string;
    category: string;
    key: ServiceSegmentType;
    productGroups: {
      name: string;
      typeName: string;
      products: ProductCardBaseData[];
    }[];
    seoFooter: {
      childMarkdownRemark: {
        html: string;
      };
    };
  };
  strapiServiceSegment: {
    content: PossibleContentSections[];
    mewaCircle: MewaCircleData | null;
  };
  translations: {
    service_segment_groups_dropdown_label: string;
    collection_slider_collection_link_label: string;
    show_product_details_label: string;
    seo_footer_show_more_label: string;
    seo_footer_show_less_label: string;
    mewa_principle_main_title: string;
    navigation_product_menu_label: string;
  };
  logoTeaser?: ServiceTeaserData<ServiceTeaserType.logoEmbroideryService>;
  customerReport?: CustomerReport;
};

type PageContext = GlobalPageContext & {
  id: string;
  market: Market;
};

export const query = graphql`
  query (
    $marketRegex: String!
    $id: String!
    $lang: String!
    $locale: String!
    $uuid: String
  ) {
    ...NavigationFragment

    pimServiceSegment(id: { eq: $id }) {
      name
      slug
      claim
      category
      key
      productGroups {
        name
        typeName
        products {
          ...ProductData
        }
      }
      seoFooter {
        childMarkdownRemark {
          html
        }
      }
    }

    strapiServiceSegment(locale: { eq: $locale }, pimUuid: { eq: $uuid }) {
      ...ContentSectionFragment

      mewaCircle {
        ...MewaCircleFragment
      }
    }

    translations: translation(locale: { eq: $locale }) {
      service_segment_groups_dropdown_label
      collection_slider_collection_link_label
      show_product_details_label
      seo_footer_show_more_label
      seo_footer_show_less_label
      mewa_principle_main_title
      navigation_product_menu_label
    }

    logoTeaser: strapiServiceTeaser(
      ServiceType: { eq: "logo-stick-service" }
      locale: { eq: $locale }
    ) {
      description: Description
      title: Title
      serviceType: ServiceType
    }

    ...CustomerReportFragment
  }
`;

const FADE_DURATION = 400;
const PAUSE = 100;

export const ClothingServiceSegment = ({
  data: {
    pimServiceSegment: serviceSegment,
    strapiServiceSegment,
    translations,
    logoTeaser,
    customerReport,
    navigationLinks,
    contactTeaserLabels,
    headerAndFooterTranslations,
  },
  pageContext: { langPrefix, locale, market, lang },
}: PageProps<Props, PageContext>) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const [renderGroupIndex, setRenderGroupIndex] = useState(0);
  const { ctaLabel } = useConversionButtonLabel({
    translations: headerAndFooterTranslations,
  });

  const handleTabChange = useCallback(
    (newValue: number) => {
      setActiveGroupIndex(newValue);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setRenderGroupIndex(newValue);
      }, FADE_DURATION + PAUSE);
    },
    [setActiveGroupIndex, setRenderGroupIndex],
  );

  const groups = serviceSegment.productGroups.map((group) => ({
    ...group,
    icon: protectiveClothingIconForType(group.typeName),
  }));

  const productsGroupedByCollection = groupByCollection(
    groups[renderGroupIndex].products,
  );

  const conversion = {
    label: ctaLabel,
    link: `${langPrefix}/${navigationLinks.conversion.contact.slug}`,
    onClick: () => {
      storeServiceSegment(serviceSegment.key);
    },
  };

  const breadCrumbData: BreadCrumbData[] = [
    {
      name: t(translations, 'navigation_product_menu_label'),
      slug: `${langPrefix}/${navigationLinks.navigation.productMenu.slug}/`,
    },
    {
      name: navigationLinks.navigation.clothing.name,
      slug: `${langPrefix}/${navigationLinks.navigation.clothing.slug}/`,
    },
    {
      name: serviceSegment.name,
      slug: '',
    },
  ];

  const mobileBreadCrumbData: BreadCrumbData = {
    name: navigationLinks.navigation.clothing.name,
    slug: `${langPrefix}/${navigationLinks.navigation.clothing.slug}/`,
  };

  return (
    <div data-pagefind-meta={`service_segment_type:${serviceSegment.key}`}>
      <BreadCrumbs
        data={breadCrumbData}
        mobileData={mobileBreadCrumbData}
        localeBaseUrl={localeBaseUrl(locale)}
        LinkComponent={GatsbyLink}
      />
      <PageHeadlineWrapper
        title={serviceSegment.name}
        subtitle={serviceSegment.claim}
        cta={conversion}
        sx={{
          ...(groups.length > 1 && {
            marginBottom: '100px',
          }),
        }}
      />
      <ResponsiveGrid>
        <Grid size={12}>
          {groups.length > 1 && (
            <StyledTabs
              name="service-segment-tabs"
              selectLabel={t(
                translations,
                'service_segment_groups_dropdown_label',
              )}
              activeIndex={activeGroupIndex}
              onChange={handleTabChange}
              items={groups}
            />
          )}
        </Grid>
      </ResponsiveGrid>

      <DeferredProductSliderList
        show={activeGroupIndex === renderGroupIndex}
        data={productsGroupedByCollection}
        index={activeGroupIndex}
        langPrefix={langPrefix}
        logoTeaser={logoTeaser}
        customerReport={customerReport}
        collectionLinkLabel={t(
          translations,
          'collection_slider_collection_link_label',
        )}
        detailsPageLabel={t(translations, 'show_product_details_label')}
      />

      <div>
        {productsGroupedByCollection.length < 3 && logoTeaser ? (
          <ServiceTeaser
            data-pagefind-ignore
            description={logoTeaser.description}
            title={logoTeaser.title}
            type={logoTeaser.serviceType}
          />
        ) : null}
      </div>

      {productsGroupedByCollection.length <= 3 ? (
        <ResponsiveGrid sx={{ marginTop: 'var(--spacer-xxxl)' }}>
          <Grid size={12}>
            <Line />
          </Grid>
        </ResponsiveGrid>
      ) : null}

      {strapiServiceSegment?.content ? (
        <ContentResponsiveGrid>
          <Grid size={12}>
            <ContentSection
              possibleComponents={possibleComponents}
              conversion={{ label: '', link: '' }}
              lang={lang}
              locale={locale}
              langPrefix={langPrefix}
              sections={strapiServiceSegment.content}
              navigationLinks={navigationLinks}
              translations={{ author_box_by: '' }}
            />
          </Grid>
        </ContentResponsiveGrid>
      ) : null}

      {strapiServiceSegment?.mewaCircle ? (
        <ResponsiveGrid>
          <Grid size={12}>
            <StyledMewaCircle
              circle={strapiServiceSegment.mewaCircle}
              title={t(translations, 'mewa_principle_main_title')}
              link={
                <Link
                  to={`${langPrefix || ''}/${navigationLinks.fullService.slug}`}
                >
                  {navigationLinks.fullService.name}
                </Link>
              }
            />
          </Grid>
        </ResponsiveGrid>
      ) : null}

      <ContactTeaserWrapper
        contactTeaserLabels={contactTeaserLabels}
        langPrefix={langPrefix}
        navigationLinks={navigationLinks}
        serviceSegment={serviceSegment.key}
      />

      {serviceSegment.seoFooter &&
        !!serviceSegment.seoFooter.childMarkdownRemark.html && (
          <SeoFooter
            html={serviceSegment.seoFooter.childMarkdownRemark.html}
            showMoreLabel={t(translations, 'seo_footer_show_more_label')}
            showLessLabel={t(translations, 'seo_footer_show_less_label')}
          />
        )}
    </div>
  );
};

ClothingServiceSegment.noContactTeaser = true;

export default ClothingServiceSegment;

const ContentResponsiveGrid = styled(ResponsiveGrid)({
  marginTop: 'var(--spacer-xxxl)',
});

const StyledMewaCircle = styled(MewaCircle)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    marginTop: 'var(--spacer-xxxl)',
  },
}));

const StyledTabs = styled(ResponsiveTabs)(({ theme }) => ({
  marginBottom: '18px',

  [theme.breakpoints.up('sm')]: {
    marginBottom: '60px',
  },
}));

const INITIAL_DISPLAY_COUNT = 2;

const DeferredProductSliderList = ({
  data,
  logoTeaser,
  customerReport,
  collectionLinkLabel,
  langPrefix,
  index,
  show,
  detailsPageLabel,
}: {
  data: CollectionProducts<ProductCardBaseData>[];
  logoTeaser?: ServiceTeaserData<ServiceTeaserType.logoEmbroideryService>;
  customerReport?: CustomerReport;
  collectionLinkLabel: string;
  langPrefix: string;
  index: number;
  show: boolean;
  detailsPageLabel: string;
}) => {
  const [displayCount, setDisplayCount] = useState(INITIAL_DISPLAY_COUNT);

  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastIndex = useRef(index);

  const cancelDisplayCountIncrement = () => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
  };

  // NOTE: Not sure if this is actually a good idea. This should save us from
  // regenerating a lot of virtual dom nodes on incremental renders but
  // probably does not have a big effect on performance as the bottleneck
  // happens after mounting the Swiper component not when generating the
  // virtual dom nodes.
  const allChildren = useMemo(
    () =>
      data.map((collection, cIdx) => (
        <React.Fragment key={cIdx}>
          <ProductsSliderContainer
            sx={{
              ':not(:first-of-type)': { marginTop: [null, '90px'] },
            }}
          >
            <CollectionSliderWrapper
              collection={collection}
              langPrefix={langPrefix}
              collectionLinkLabel={collectionLinkLabel}
              detailsPageLabel={detailsPageLabel}
            />
          </ProductsSliderContainer>
          {cIdx === 2 && logoTeaser && (
            <ServiceTeaser
              data-pagefind-ignore
              description={logoTeaser.description}
              title={logoTeaser.title}
              type={logoTeaser.serviceType}
            />
          )}
        </React.Fragment>
      )),
    [data, collectionLinkLabel, langPrefix, logoTeaser, detailsPageLabel],
  );

  // When the displayCount changes we check if there are any sliders not yet
  // displayed. If that is the case, we increment the display count. This will
  // effectively loop with a delay until all the sliders are displayed.
  //
  // This effect is also run if `data` + `index` changes. In that case, the
  // display count will be reset to the initial value to make sure switching
  // tabs works quickly.
  useEffect(() => {
    cancelDisplayCountIncrement();

    if (lastIndex.current !== index) {
      // changed to new tab
      if (displayCount === INITIAL_DISPLAY_COUNT) {
        if (displayCount < data.length) {
          timeoutId.current = setTimeout(() => {
            setDisplayCount((c) => c + 1);
          }, 200);
        }
      } else {
        //
        setDisplayCount(INITIAL_DISPLAY_COUNT);
      }
    } else {
      // render existing tab
      if (displayCount < data.length) {
        timeoutId.current = setTimeout(() => {
          setDisplayCount((c) => c + 1);
        }, 200);
      }
    }

    lastIndex.current = index;

    return () => {
      cancelDisplayCountIncrement();
    };
  }, [displayCount, setDisplayCount, index, data]);

  // Create a new array only containing the children which should be rendered.
  const childrenToRender = allChildren.slice(0, displayCount);

  return (
    <FadeContainer style={{ opacity: show ? 1 : 0 }}>
      {childrenToRender}
    </FadeContainer>
  );
};

const FadeContainer = styled('div')({
  transition: `opacity ${FADE_DURATION}ms ease`,
});
